"use client"

import type { Numberish } from "@/models/common.model"
import { Check, ChevronDown, Search, X } from "lucide-react"
import { useTranslations } from "next-intl"
import * as React from "react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { cn } from "@/lib/utils"

type ExtendedComboboxProps<T extends { value: Numberish, label: string, disabled?: boolean }> = {
  items?: T[]
  placeholder?: string
  label?: string
  clearable?: boolean
  popoverAlign?: "start" | "center" | "end"
  allowDeselect?: boolean
  disabled?: boolean
  multiple?: boolean
  min?: Numberish
  max?: Numberish
  zIndex?: Numberish
  value?: Numberish | Numberish[]
  loading?: boolean
  modal?: boolean
  onChange?: (value: Numberish | Numberish[]) => void
  renderItemLabel?: (item: T) => React.ReactNode
}
type DefaultItems<T> = T[] extends { value: Numberish, label: string, disabled?: boolean } ? T[] : never

const DEFAULT_ITEMS: DefaultItems<{ value: Numberish, label: string, disabled?: boolean }>[] = []

export function ExtendedCombobox<T extends { value: Numberish, label: string, disabled?: boolean }>({
  items = DEFAULT_ITEMS,
  placeholder = "Select an item",
  label,
  clearable = true,
  popoverAlign = "center",
  allowDeselect = true,
  disabled = false,
  multiple = false,
  loading = false,
  min,
  max,
  zIndex,
  value,
  onChange,
  renderItemLabel,
  modal = false,
}: ExtendedComboboxProps<T>) {
  const t = useTranslations("generic")
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState("")

  const currentValue = React.useMemo(() => {
    if (multiple && Array.isArray(value)) {
      if (value.length === 0)
        return placeholder
      if (value.length <= 3) {
        return value.map(v => items?.find(item => item.value.toString() === v.toString())?.label).filter(Boolean)
      }
      return `${value.length} selected`
    }
    return value
      ? items?.find(item => item.value.toString() === value.toString())?.label
      : placeholder
  }, [value, items, multiple, placeholder])

  const filteredItems = React.useMemo(() => {
    return items?.filter(item =>
      item.label.toLowerCase().includes(search.toLowerCase()),
    )
  }, [items, search])

  const constraintsText = React.useMemo(() => {
    if (!multiple)
      return ""
    const current = Array.isArray(value) ? value.length : 0
    const remaining = max ? Number(max) - current : null
    const minText = min ? `Min: ${min}` : ""
    const maxText = max ? `Max: ${max}` : ""
    const remainingText = remaining !== null ? `(Remaining: ${remaining})` : ""

    return [minText, maxText, remainingText].filter(Boolean).join(" • ")
  }, [multiple, value, min, max])

  const shouldDisableItem = React.useCallback((item: T) => {
    if (!multiple || !Array.isArray(value))
      return false
    if (value.find(i => i === item.value))
      return false
    if (max && value.length >= Number(max))
      return true
    return false
  }, [multiple, value, max])

  const handleSelect = React.useCallback((item: T) => {
    if (multiple) {
      if (Array.isArray(value)) {
        const index = value.indexOf(item.value)
        if (index > -1) {
          onChange?.(value.filter((_, i) => i !== index))
        }
        else {
          if (!max || value.length < Number(max)) {
            onChange?.([...value, item.value])
          }
        }
      }
      else {
        onChange?.([item.value])
      }
    }
    else {
      if (allowDeselect) {
        onChange?.(value === item.value ? "" : item.value)
      }
      else {
        onChange?.(item.value)
      }
      setOpen(false)
    }
  }, [multiple, value, max, allowDeselect, onChange])

  const handleClear = React.useCallback(() => {
    if (multiple && Array.isArray(value)) {
      onChange?.([])
    }
    else {
      onChange?.("")
    }
  }, [multiple, value, onChange])

  return (
    <Popover
      modal={modal}
      onOpenChange={setOpen}
      open={open}
    >
      <div className="flex flex-1 flex-col gap-2">
        {label && <Label>{label}</Label>}
        <PopoverTrigger asChild>
          <Button
            aria-expanded={open}
            className="h-auto w-full justify-between"
            disabled={disabled}
            role="combobox"
            variant="outline"
          >
            <div
              className={cn(
                "max-w-[calc(100%-3rem)] overflow-x-hidden truncate text-left",
                currentValue === placeholder ? "styled-placeholder" : "",
              )}
            >
              {!loading
                ? (
                    multiple && Array.isArray(value) && value.length > 0
                      ? (
                          <div className="flex flex-wrap gap-1">
                            {value.slice(0, 3).map(v => (
                              <Badge
                                key={String(v)}
                                className="mr-1"
                                variant="secondary"
                              >
                                {items?.find(item => item.value.toString() === v.toString())?.label}
                              </Badge>
                            ))}
                            {value.length > 3 && (
                              <span className="text-muted-foreground">
                                {t("more", { count: value.length - 3 })}
                              </span>
                            )}
                          </div>
                        )
                      : (
                          currentValue
                        )
                  )
                : (
                    "loading..."
                  )}
            </div>
            <div className="ml-2 flex shrink-0 items-center">
              {value && clearable && allowDeselect && (
                <div
                  className="flex cursor-pointer items-center justify-center rounded-full p-1 text-destructive transition-colors hover:bg-destructive/20"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleClear()
                  }}
                >
                  <X className="size-4 shrink-0" />
                </div>
              )}
              <ChevronDown className="ml-2 size-4 shrink-0 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>
        {constraintsText && (
          <p className="mt-1 text-sm text-muted-foreground">
            {constraintsText}
          </p>
        )}
      </div>
      <PopoverContent
        align={popoverAlign}
        className={cn("p-0", zIndex ? `z-[${zIndex}]` : "")}
      >
        <Command>
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 size-4 shrink-0 opacity-50" />
            <Input
              autoFocus
              className={cn(
                "flex h-11 bg-transparent w-full py-3 text-sm outline-none border-none ring-0 ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50",
              )}
              onChange={e => setSearch(e.target.value)}
              placeholder={placeholder}
              value={search}
            />
          </div>
          {(!filteredItems || filteredItems.length === 0)
            ? (
                <CommandEmpty>{t("not_found")}</CommandEmpty>
              )
            : (
                <CommandList>
                  <CommandGroup>
                    {filteredItems.map(item => (
                      <CommandItem
                        key={item.value.toString()}
                        disabled={item.disabled || shouldDisableItem(item)}
                        onSelect={() => handleSelect(item)}
                        value={item.value.toString()}
                      >
                        {renderItemLabel ? renderItemLabel(item) : item.label}
                        <Check
                          className={cn(
                            "ml-auto h-4 w-4",
                            multiple
                              ? Array.isArray(value)
                                ? value.includes(item.value)
                                  ? "opacity-100"
                                  : "opacity-0"
                                : value === item.value
                                  ? "opacity-100"
                                  : "opacity-0"
                              : value === item.value
                                ? "opacity-100"
                                : "opacity-0",
                          )}
                        />
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              )}
        </Command>
      </PopoverContent>
    </Popover>
  )
}
